import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  TextArea,
  useToast,
} from "@vms/ui-web";
import { useApi, useAuth } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";
import { useQuery } from "../api/queries";
import type { VenueListItem } from "../api/types";

interface Release {
  id: string;
  component: string;
  platform?: string | null;
  version: string;
  channel: "stable" | "beta" | "dev";
  artifactUrl: string;
  sha256: string;
  notes?: string | null;
  /** The cloud Release model field — column `released_at`, camelCased. */
  releasedAt: string;
}

/** Format a release timestamp, tolerating MySQL "YYYY-MM-DD HH:MM:SS" (which
 *  some browsers parse as Invalid Date) and missing values. */
function fmtReleasedAt(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

// LAN-pushed components — the ones this catalogue + pin flow actually deploy
// via the hub relay. Mobile apps (driver-registration / sim-manager, iOS +
// Android) ship through the App Store / alternative channels, so they're
// deliberately NOT shown here even if a release row exists for version tracking.
const COMPONENTS = [
  "hub",
  "hub-ui-server",
  "agent",
  "server-agent",
  "server-agent-cli",
  "display-agent",
  "vms-updater",
];

const MOBILE_COMPONENTS = new Set([
  "driver-registration-ios",
  "driver-registration-android",
  "sim-manager-ios",
  "sim-manager-android",
]);

// Friendly box labels (the raw component id is kept as a sub-label). Matches
// the upload modal's target names so "Hub UI" reads as you'd expect.
const COMPONENT_LABEL: Record<string, string> = {
  hub: "Hub",
  "hub-ui-server": "Hub UI",
  agent: "Sim Agent",
  "server-agent": "Server Agent (Windows)",
  "server-agent-cli": "Server Agent (Linux)",
  "display-agent": "Display Agent",
  "vms-updater": "Updater",
};

const PLATFORM_LABEL: Record<string, string> = {
  "win32-x64": "Windows x64",
  "linux-x64": "Linux x64",
  "linux-arm64": "Linux arm64",
};

/** One uploadable artifact: a (component, platform) pair = one release row. */
interface ReleaseSlot {
  platform: string;
  component: string;
}

/** A release target the operator picks. Selecting it reveals one upload slot
 *  per platform that target ships. Most targets map every slot to a single
 *  component; "Server Agent" fans out across two (Windows shell vs Linux CLI). */
interface ReleaseTarget {
  id: string;
  label: string;
  slots: ReleaseSlot[];
}

const RELEASE_TARGETS: ReleaseTarget[] = [
  {
    id: "hub",
    label: "Hub",
    slots: [
      { platform: "win32-x64", component: "hub" },
      { platform: "linux-x64", component: "hub" },
      { platform: "linux-arm64", component: "hub" },
    ],
  },
  {
    id: "hub-ui",
    label: "Hub UI",
    slots: [
      { platform: "win32-x64", component: "hub-ui-server" },
      { platform: "linux-x64", component: "hub-ui-server" },
      { platform: "linux-arm64", component: "hub-ui-server" },
    ],
  },
  {
    id: "agent",
    label: "Sim Agent",
    slots: [{ platform: "win32-x64", component: "agent" }],
  },
  {
    id: "server-agent",
    label: "Server Agent",
    slots: [
      { platform: "win32-x64", component: "server-agent" },
      { platform: "linux-x64", component: "server-agent-cli" },
    ],
  },
  {
    id: "display-agent",
    label: "Display Agent",
    slots: [
      { platform: "linux-arm64", component: "display-agent" },
      { platform: "win32-x64", component: "display-agent" },
    ],
  },
  {
    id: "vms-updater",
    label: "Updater",
    slots: [
      { platform: "win32-x64", component: "vms-updater" },
      { platform: "linux-x64", component: "vms-updater" },
      { platform: "linux-arm64", component: "vms-updater" },
    ],
  },
];

const slotKey = (s: ReleaseSlot) => `${s.component}:${s.platform}`;

export function Releases() {
  const releases = useQuery<Release[]>("/api/releases");
  const [pinning, setPinning] = useState<Release | null>(null);
  const [uploading, setUploading] = useState(false);

  const grouped = useMemo(() => {
    const out: Record<string, Release[]> = {};
    for (const r of releases.data ?? []) {
      if (!out[r.component]) out[r.component] = [];
      out[r.component].push(r);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => +new Date(b.releasedAt) - +new Date(a.releasedAt));
    }
    return out;
  }, [releases.data]);

  // Show a box for every known LAN component, plus any unknown component that
  // turns up in the data (so a future component isn't hidden) — but never the
  // mobile/App-Store components, which deploy through other channels.
  const displayedComponents = useMemo(() => {
    const extras = Object.keys(grouped).filter(
      (c) => !COMPONENTS.includes(c) && !MOBILE_COMPONENTS.has(c),
    );
    return [...COMPONENTS, ...extras];
  }, [grouped]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Releases"
        subtitle="Component release catalogue. Pin a release to push it to a venue or organisation."
        actions={
          <Button onClick={() => setUploading(true)}>Upload release</Button>
        }
      />

      <DataState loading={releases.loading} error={releases.error}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {displayedComponents.map((component) => {
            const list = grouped[component] ?? [];
            return (
              <Card key={component} className="overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {COMPONENT_LABEL[component] ?? component}
                    </div>
                    <div className="font-mono text-xs text-gray-400">{component}</div>
                  </div>
                  <span className="text-xs text-gray-500">{list.length} releases</span>
                </div>
                {list.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-gray-500">None published yet.</div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {list.slice(0, 5).map((r) => (
                      <li key={r.id} className="px-5 py-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            v{r.version}
                            {r.platform && (
                              <span className="ml-2 font-mono text-xs text-gray-500">{r.platform}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {fmtReleasedAt(r.releasedAt)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            className={
                              r.channel === "dev"
                                ? "bg-purple-100 text-purple-700"
                                : r.channel === "beta"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-green-100 text-green-700"
                            }
                          >
                            {r.channel}
                          </Badge>
                          <Button size="sm" variant="secondary" onClick={() => setPinning(r)}>
                            Pin →
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      </DataState>

      <PinReleaseModal release={pinning} onClose={() => setPinning(null)} />
      <UploadReleaseModal
        isOpen={uploading}
        onClose={() => setUploading(false)}
        onUploaded={() => {
          setUploading(false);
          releases.refetch();
        }}
      />
    </div>
  );
}

function PinReleaseModal({
  release,
  onClose,
}: {
  release: Release | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { orgs } = useOrgContext();
  const { user } = useAuth();
  const { addToast } = useToast();
  type PinScope = "venue" | "organisation" | "all";
  const [scope, setScope] = useState<PinScope>("venue");
  const [orgId, setOrgId] = useState<string>("");
  const [venueId, setVenueId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [allVenues, setAllVenues] = useState<VenueListItem[]>([]);

  useEffect(() => {
    if (!release) return;
    void api("/api/venues")
      .then((r) => r.json())
      .then((j) => setAllVenues(j?.data ?? []))
      .catch(() => setAllVenues([]));
  }, [api, release]);

  async function submit() {
    if (!release) return;
    if (scope === "venue" && !venueId) return;
    if (scope === "organisation" && !orgId) return;
    setSubmitting(true);
    try {
      const target =
        scope === "all"
          ? { allOrganisations: true }
          : scope === "venue"
            ? { venueId }
            : { organisationId: orgId };
      const res = await api("/api/releases/pin", {
        method: "POST",
        body: JSON.stringify({ releaseId: release.id, ...target }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to pin release", "error");
        return;
      }
      const scopeLabel =
        scope === "all" ? `all organisations (${json.data.organisationsPinned})` : "";
      addToast(
        `Pinned ${release.component} v${release.version}${scopeLabel ? ` to ${scopeLabel}` : ""} (${
          json.data.hubsNotified
        } hub${json.data.hubsNotified === 1 ? "" : "s"} notified)`,
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const visibleVenues = orgId ? allVenues.filter((v) => v.organisationId === orgId) : allVenues;

  return (
    <Modal isOpen={!!release} onClose={onClose} title="Pin release">
      {release && (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Pinning <span className="font-mono font-medium">{release.component}</span> v
            {release.version} ({release.channel}). Pinning replaces any existing pin for this
            component within the chosen scope.
          </div>

          <Select
            label="Scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as PinScope)}
            options={[
              { value: "venue", label: "Specific venue" },
              { value: "organisation", label: "Whole organisation" },
              // Pushing to every org is a cross-tenant action — FPZERO only.
              ...(user?.isFpzeroAdmin ? [{ value: "all", label: "All organisations" }] : []),
            ]}
          />

          {scope === "all" && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Pins this release for <strong>every organisation</strong> and notifies all their
              connected hubs. Replaces each org&apos;s existing pin for this component.
            </div>
          )}

          {scope !== "all" && (
            <Select
              label="Organisation"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setVenueId("");
              }}
              options={[
                { value: "", label: "—" },
                ...orgs.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          )}

          {scope === "venue" && (
            <Select
              label="Venue"
              value={venueId}
              onChange={(e) => setVenueId(e.target.value)}
              options={[
                { value: "", label: orgId ? "—" : "Select an organisation first" },
                ...visibleVenues.map((v) => ({ value: v.id, label: v.name })),
              ]}
              disabled={!orgId}
            />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              loading={submitting}
              disabled={
                (scope === "venue" && !venueId) || (scope === "organisation" && !orgId)
              }
            >
              Pin
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Upload a built artifact from the operator's disk straight into the cloud.
 * Used for Windows .exe components (sim agent, server-agent) which need a
 * Windows host to build and so can't run through the Linux Jenkins pipeline.
 *
 * Uses XMLHttpRequest rather than fetch so the upload `progress` event is
 * available — fetch's whatwg-streams ReadableStream is theoretically usable
 * for progress but isn't supported by all browsers we care about.
 */
interface SlotResult {
  state: "uploading" | "done" | "failed";
  progress: number;
  error?: string;
}

function UploadReleaseModal({
  isOpen,
  onClose,
  onUploaded,
}: {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [targetId, setTargetId] = useState<string>(RELEASE_TARGETS[0].id);
  // One File per slot, keyed by slotKey(). A target's platforms each get a slot.
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState<"stable" | "beta" | "dev">("stable");
  const [minProto, setMinProto] = useState("1");
  const [maxProto, setMaxProto] = useState("1");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<Record<string, SlotResult>>({});
  const [error, setError] = useState<string | null>(null);
  // Bumping this remounts the native <input type=file> elements so changing
  // target (or reopening) visibly clears any previously-picked files.
  const [formNonce, setFormNonce] = useState(0);

  const target = RELEASE_TARGETS.find((t) => t.id === targetId) ?? RELEASE_TARGETS[0];

  function resetForm() {
    setTargetId(RELEASE_TARGETS[0].id);
    setFiles({});
    setVersion("");
    setChannel("stable");
    setMinProto("1");
    setMaxProto("1");
    setNotes("");
    setUploading(false);
    setResults({});
    setError(null);
    setFormNonce((n) => n + 1);
  }

  // Reset whenever the modal opens — no stale state from a prior attempt.
  useEffect(() => {
    if (isOpen) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /** Upload one slot's file via the per-(component,platform) endpoint.
   *  Resolves true on success. Updates the slot's live status as it goes. */
  function uploadSlot(slot: ReleaseSlot, file: File): Promise<boolean> {
    const key = slotKey(slot);
    return new Promise((resolve) => {
      setResults((r) => ({ ...r, [key]: { state: "uploading", progress: 0 } }));
      const form = new FormData();
      form.append("file", file);
      form.append("component", slot.component);
      form.append("platform", slot.platform);
      form.append("version", version);
      form.append("channel", channel);
      form.append("minProtocolVersion", minProto);
      form.append("maxProtocolVersion", maxProto);
      if (notes) form.append("notes", notes);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/releases/upload");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setResults((r) => ({ ...r, [key]: { state: "uploading", progress } }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setResults((r) => ({ ...r, [key]: { state: "done", progress: 100 } }));
          resolve(true);
        } else {
          let msg = `HTTP ${xhr.status}`;
          try {
            const body = JSON.parse(xhr.responseText) as { error?: string };
            if (body.error) msg = body.error;
          } catch {
            /* fall back to status */
          }
          setResults((r) => ({ ...r, [key]: { state: "failed", progress: 0, error: msg } }));
          resolve(false);
        }
      };
      xhr.onerror = () => {
        setResults((r) => ({ ...r, [key]: { state: "failed", progress: 0, error: "Network error" } }));
        resolve(false);
      };
      xhr.send(form);
    });
  }

  async function submit() {
    if (!/^[0-9]+\.[0-9]+\.[0-9]+$/.test(version)) {
      setError("Version must be semver (e.g. 0.1.25)");
      return;
    }
    const chosen = target.slots.filter((s) => files[slotKey(s)]);
    if (chosen.length === 0) {
      setError("Pick at least one artifact file");
      return;
    }
    setError(null);
    setResults({});
    setUploading(true);
    const outcomes = await Promise.all(chosen.map((s) => uploadSlot(s, files[slotKey(s)]!)));
    setUploading(false);

    const okCount = outcomes.filter(Boolean).length;
    if (okCount > 0) {
      addToast(`Uploaded ${okCount}/${chosen.length} ${target.label} artifact(s) v${version}`);
      onUploaded();
    }
    // All succeeded → close. Any failure → keep open so the per-slot errors
    // are visible and the operator can fix + re-submit just those.
    if (okCount === chosen.length) onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={uploading ? () => undefined : onClose} title="Upload release" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Pick a target, then drop in the built artifact for each platform it ships. Each
          becomes its own release; you don&apos;t need every platform at once.
        </p>

        <Select
          label="Target"
          value={targetId}
          onChange={(e) => {
            setTargetId(e.target.value);
            setFiles({});
            setResults({});
            setError(null);
            setFormNonce((n) => n + 1);
          }}
          disabled={uploading}
          options={RELEASE_TARGETS.map((t) => ({ value: t.id, label: t.label }))}
        />

        {/* One upload slot per platform this target ships. They sit in a row
            when there's room and WRAP (rather than cram) once a slot would fall
            below ~14rem — so a narrow modal degrades to 2-per-row then stacked
            instead of squeezing into "Windo…". */}
        <div className="flex flex-wrap gap-3">
          {target.slots.map((slot) => {
            const key = slotKey(slot);
            const file = files[key] ?? null;
            const result = results[key];
            return (
              <div key={`${formNonce}-${key}`} className="grow basis-[15rem] min-w-[14rem] border border-gray-200 rounded-md px-3 py-2">
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {PLATFORM_LABEL[slot.platform] ?? slot.platform}
                  </span>
                  <span className="font-mono text-[10px] text-gray-400 truncate">{slot.component}</span>
                </div>
                <input
                  type="file"
                  disabled={uploading}
                  onChange={(e) => setFiles((f) => ({ ...f, [key]: e.target.files?.[0] ?? null }))}
                  className="block w-full text-xs text-gray-700 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 file:cursor-pointer"
                />
                {file && !result && (
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
                  </p>
                )}
                {result?.state === "uploading" && (
                  <div className="mt-1.5 h-1.5 bg-gray-200 rounded overflow-hidden">
                    <div className="h-full bg-indigo-600 transition-[width] duration-150" style={{ width: `${result.progress}%` }} />
                  </div>
                )}
                {result?.state === "done" && <p className="text-xs text-green-700 mt-1">✓ Uploaded</p>}
                {result?.state === "failed" && <p className="text-xs text-red-700 mt-1 truncate" title={result.error}>✗ {result.error}</p>}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Input
            label="Version (semver)"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={uploading}
            placeholder="0.1.25"
          />
          <Select
            label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as "stable" | "beta" | "dev")}
            disabled={uploading}
            options={[
              { value: "stable", label: "stable" },
              { value: "beta", label: "beta" },
              { value: "dev", label: "dev" },
            ]}
          />
          <Input
            label="Min protocol version"
            type="number"
            value={minProto}
            onChange={(e) => setMinProto(e.target.value)}
            disabled={uploading}
          />
          <Input
            label="Max protocol version"
            type="number"
            value={maxProto}
            onChange={(e) => setMaxProto(e.target.value)}
            disabled={uploading}
          />
        </div>

        <TextArea
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={uploading}
          rows={2}
          placeholder="e.g. Lap-timing fixes, AC C# bridge rebuild"
        />

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={submit} loading={uploading} disabled={Object.values(files).every((f) => !f)}>
            Upload &amp; register
          </Button>
        </div>
      </div>
    </Modal>
  );
}
